import { IonHeader, IonIcon, IonSpinner } from "@ionic/react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { chevronDownOutline } from "ionicons/icons";
import OrderService from "../../../services/OrderService";
import { NavLink } from "react-router-dom";
import withNav from "../../../utils/hoc/withNav";
import Page from "../../../components/Page";
import ATHS from "../../../components/ATHS";
import ToastService from "../../../services/ToastService";
import { OrderStatuses, TaskStatuses, orderStatusMap, taskStatusMap } from "../../../config";
import { SPECIAL_ERRORS } from "../../../config/error";
import ErrorComponent from "../../../components/Error";
import useIsAppOffline from "../../../utils/hooks/useIsOffline";
import ExpandableCTA from "../../../components/ExpandableCTA";
import { fancyTimeFormat } from "../../../utils/date";
import alert from '../../../assets/alert.webp';
import { renderDishContents } from "../../../utils/parsers/dishContents";
import BottomCTA from "../../../components/BottomCTA";
import { Modal, ModalButtonGroup, ModalHeader, ModalOverlay, ModalPrimaryButton, ModalSubtitle } from "../../../components/Modal";
import { TrackOrderProps } from "../../../types/trackOrderProps";
import { isEmptyObject } from "../../../utils/objects";
import ActiveOrderMessage from "../components/ActiveOrderMessage";

const PollingPeriods: any = {
	[OrderStatuses.CREATED]: {
		pollingPeriod: 25 * 1000,
	},
	[OrderStatuses.IN_QUEUE]: {
		pollingPeriod: 25 * 1000,
	},
	[OrderStatuses.IN_PROGRESS]: {
		pollingPeriod: 10 * 1000,
		dependentonItemStatuses: true,
		itemStatusPollingPeriods: {
			[TaskStatuses.READY_TO_COLLECT]: 10 * 1000,
			[TaskStatuses.COLLECTING]: 5 * 1000,
			[TaskStatuses.COLLECT_INITIATED]: 2 * 1000,
		},
	},
	DEFAULT: {
		pollingPeriod: 25 * 1000,
	},
};

const OrderItems = styled.div`
	display: flex;
	flex-direction: column;
	height: max-content;
	width: 100%;
`;

const Center = styled.div`
	display: flex;
	flex-direction: column;
	text-align: center;
	align-items: center;
	justify-content: center;
	height: 100%;
	width: 100%;
`;

const Error = styled.div`
	font-weight: 600;
	font-size: 20px;
	color: #fff;
	width: 100%;
`;

const Header = styled.div`
	text-align: left;
	font-weight: 600;
	font-size: 20px;
	color: #fff;
	width: 100%;
`;

const PageContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: max-content;
	width: 100%;
	padding: 0 1rem;
	margin: 1rem 0 0 0;
`;

const PageHeader = styled(IonHeader)`
	position: fixed;
	margin: 0 0 0 0;
	background: ${({ theme }) => theme.colors.main.background};
	font-family: inherit;
	font-style: normal;
	font-size: 20px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 1.5rem 1rem;
	border: none;
	box-shadow: none;
	&:after {
		background-image: none;
	}
`;

const BackButton = styled(NavLink)`
	
`;

const BackButtonImg = styled(IonIcon)`
	display:flex;
	justify-content:center;
	align-items:center;
	width: 24px;
	height: 24px;
	color: #fff;
	/* Icon alignment */
	margin-left: -5px;
`;

const PageTitle = styled.div`
	/* Keeping it centered becuase of icon alignment */
	padding-left: 5px;
	font-style: normal;
	font-weight: 900;
	font-size: 20px;
	letter-spacing: 0px;
	display: flex;
	justify-content: center;
`;

const HelpButton = styled(IonIcon)`
	width: 20px;
	height: 20px;
	margin-left: auto;
`;

const SmallSpace = styled.div`
	margin: 4rem 0 0 0;
`;

const ActiveOrders = styled.div`
	color: #f2f2f2;
	font-size: 12px;
	font-weight: 600;
	width: 100%;
	text-align: left;
`;

const OrderDetails = styled.div<any>`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	margin: 0.2rem 0 0 0;
	${(props) =>
		props.centered &&
		css`
			justify-content: center;
		`}
`;

const OrderId = styled.div`
	font-weight: 700;
	font-size: 18px;
	color: #f2f2f2;
`;

const OrderStatus = styled.div<any>`
	width: max-content;
	min-width: 86px;
	padding: 0 0.3rem;
	height: 27px;
	font-weight: 500;
	font-size: 10px;
	border: 1px solid ${(props) => props.color};
	color: ${(props) => props.color};
	display: flex;
	justify-content: center;
	align-items: center;
`;

const KioskDetailsContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.2rem;
`

const KioskDetails = styled.div<any>`
	display: block;
	overflow: hidden;
	font-weight: 400;
	font-size: 11px;
	color: #ffffff;
	align-self: flex-start;
	text-align: left;
`;

const Dish = styled.div<any>`
	padding: 1rem 0;
	display: flex;
	flex-direction: row;
	width: 100%;
	height: max-content;
	overflow: hidden;
	transition: 300ms all ease-in-out;
	&:not(:first-child) {
		border-top: 1px solid #f2f2f2;
	}
`;

const BrandDetails = styled.div`
	display: flex;
	align-items: flex-start;
	height: 100%;
	width: max-content;
	padding: 0 1rem 0 0;
`;

const BrandLogo = styled.img`
	width: 34px;
	height: 34px;
	border-radius: 4px;
`;

const DishDetails = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: max-content;
`;

const Row = styled.div<any>`
	display: flex;
	width: 100%;
	max-height: ${(props) => props.maxHeight};
	height: ${(props) => props.height || "max-content"};
	justify-content: space-between;
	margin: ${(props) => props.margin};
	overflow: hidden;
	transition: 500ms max-height cubic-bezier(0, 1, 0, 1);
	-webkit-line-clamp: ${(props) => props.lineClamp};
`;

const CenteredRow = styled.div`
	display: flex;
	width: max-content;
	height: max-content;
	align-items: center;
	width: 40%;
`;

const DishTitle = styled.div`
	font-weight: 700;
	font-size: 18px;
	width: 70%;
	height: max-content;
	color: #ffffff;
	text-align: left;
	line-height: 21px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const DishStatus = styled.div<any>`
	font-weight: 400;
	font-size: 12px;
	color: ${(props) => props.color};
	text-align: center;
	margin: 8px 0 0 auto;
`;

const Arrow = styled(IonIcon)`
	width: 18px;
	height: 18px;
	color: #f2f2f2;
	margin: 0 0 0 0;
	transition: 200ms all ease-in-out;
`;

const Line = styled.div`
	margin: 0.5rem 0 0 0;
	height: 5px;
	width: 40px;
	background: #ffcc00;
`;

const Content = styled.div`
	margin: 0.5rem 0 0 0;
	display: flex;
	width: 100%;
`;

const Point = styled.div<any>`
	font-weight: 500;
	font-size: 14px;
	color: ${(props) => (props.caution ? "#FF6B4B" : "#E8E8E8")};
	min-width: max-content;
`;

const Data = styled.div`
	color: #e8e8e8;
	font-weight: 400;
	font-size: 14px;
	text-align: left;
	display: block;
	overflow: hidden;
	width: calc(50%);
	white-space: nowrap;
	text-overflow: ellipsis;
`;

const ShutterComponent = styled.div`
	margin: 1rem 0 0 0;
	display: flex;
	justify-content: space-between;
`;

const TimerComponent = styled.div<any>`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: ${(props) => (props.active ? "space-between" : "center")};
	height: 100%;
`;

const Time = styled.div`
	text-align: left;
	font-weight: 500;
	font-size: 22px;
	color: #ffcc00;
`;

const TimerText = styled.div`
	color: #d6d6d6;
	font-weight: 500;
	font-size: 10px;
	margin: 0.3rem 0 0 0;
	line-height: 16px;
`;

const Text = styled.div`
	color: #aaaaaa;
	font-weight: 500;
	font-size: 12px;
	margin: 0.3rem 0 0 0;
	line-height: 16px;
`;

const OpenShutterButton = styled.div<any>`
	background: #ffcc00;
	border: 1px solid #ffcc00;
	color: #000;
	border-radius: 4px;
	width: 141px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 500;
	font-size: 13px;
	text-transform: uppercase;
	${(props) =>
		props.disabled &&
		css`
			pointer-events: none;
		`}
`;

const TopCTA = styled.div`
	width: 100%;
	border-radius: 4px;
	background: #000;
	color: #f2f2f2;
	z-index: 105;
	padding: 1rem;
	display: flex;
	flex-direction: row;
	margin: 0 0 0 0;
`;

const SecondaryCTA = styled.div<any>`
	height: 52px;
	width: 100%;
	flex-shrink: 0;
	border-radius: 4px;
	background: #000;
	color: #f2f2f2;
	z-index: 105;
	padding: 0 1rem;
`;

const CartItemsCard = styled.div`
	background: #737373;
	height: max-content;
	width: 100%;
	display: flex;
	flex-direction: column;
	border-radius: 6px;
	margin: 1rem 0 0 0;
	padding: 0 1rem;
`;

const CenteredContainer = styled.div`
	margin: 2rem 0 0 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	height: 100%;
	width: 100%;
`;

const StatusHead = styled.div<any>`
	font-weight: 500;
	font-size: 22px;
	color: ${(props) => props.color};
`;

const BigSpace = styled.div`
	margin: 4rem 0 0 0;
`;

const SmallerSpace = styled.div`
	margin: 1rem 0 0 0;
`;

const Icon = styled.svg`
	width: 175px;
	height: 175px;
	align-self: center;
	margin: 2rem 0 0 0;
`;

const Message = styled.div`
	width: 70%;
	font-size: 16px;
	font-weight: 500;
	line-height: 24px;
`;

const SmallMessage = styled.div`
	font-size: 10px;
	font-weight: 400;
	color: #d6d6d6;
	width: 70%;
`;

const Link = styled(NavLink)`
	color: #f2f2f2;
	text-decoration: underline;
	font-weight: 500;
	font-size: 20px;
`;

const BottomContainer = styled.div<any>`
	position: absolute;
	bottom: 0;
	display: flex;
	flex-direction: column;
	height: max-content;
	width: 100%;
	${(props) =>
		props.hide &&
		css`
			display: none;
			pointer-events: none;
		`};
`;

const Gap = styled.div<any>`
	margin-top: ${(props) => props.value || "2rem"};
`;

const cardTextExpandedClass = "card-expanded";
const cardArrowExpandedClass = "card-expanded-arrow";
const textIdSuffix = "-text";
const arrowIdSuffix = "-arrow";

const maxRequestErrorsAllowed = 3;

const TrackOrder = (props: TrackOrderProps) => {
	const { kioskId, initialOrderData, orderId, uuid, errorMetaDataHandler } = props;

	const isAppOffline = useIsAppOffline();

	const [orderData, setOrderData] = useState<any>(initialOrderData || {});
	const [error, setError] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [isShutterOpenVisible, toggleShutterOpen] = useState<boolean>(false);
	const [hasActiveItem, toggleHasActiveItem] = useState<boolean>(false);
	const [isShutterOpenDisabled, disableShutterOpen] = useState<boolean>(false);
	const [shutterOpenPayload, setShutterOpenPayload] = useState<any>();
	const [errorCode, setErrorCode] = useState<any>();
	const [plateCollectionTime, setPlateCollectionTime] = useState<string>("-");
	const [shutterSuccessModal, showShutterSuccessModal] = useState<boolean>(false);

	//Using Refs for polling, Timer and Shutter events as we don't want to cause any unnecessary renders due to it.

	const pollingId = useRef<any>(0);
	const pollingPeriod = useRef<number>(0);
	const isTimerRunning = useRef<boolean>(false);
	const TimerInterval = useRef<any>();
	const Timer = useRef<any>();
	const collectionTime = useRef<any>(0);
	const consecutiveErrors = useRef<number>(0);

	useEffect(() => {
		return () => {
			stopPolling();
		};
	}, []);

	useEffect(() => {
		if (isAppOffline) {
			setErrorCode(SPECIAL_ERRORS.NETWORK_ERROR);
			setError(true);
		} 
	}, [isAppOffline]);

	useEffect(() => {
        if(isEmptyObject(orderData)) return;
		//Check for current order Status.
		const { status, orderItems } = orderData;
		if (!(status in orderStatusMap)) {
			stopPolling();
			setError(true);
			return;
		}
		handlePolling(status, orderItems);
    }, [orderData])

	useLayoutEffect(() => {
		if (!orderData) return;

		const { status } = orderData;

		if (status === OrderStatuses.ABORTED || status === OrderStatuses.COMPLETED || status === OrderStatuses.PARTIALLY_COMPLETED) {
			const script = document.createElement("script");
			script.src = "https://embed.typeform.com/next/embed.js";
			script.async = true;
			script.id = "form-script";
			document.body.appendChild(script);
		} else {
			const scriptElement = document.getElementById("form-script");
			if (scriptElement) {
				document.body.removeChild(scriptElement);
			}
		}

		return () => {
			const scriptElement = document.getElementById("form-script");
			if (scriptElement) {
				document.body.removeChild(scriptElement);
			}
		};
	}, [orderData]);

	/* Polling Functions */

	function startPolling() {
		pollingId.current = setInterval(getOrderData, pollingPeriod.current);
	}

	function updatePollingPeriod() {
		clearInterval(pollingId.current);
		pollingId.current = setInterval(getOrderData, pollingPeriod.current);
	}

	function stopPolling() {
		clearInterval(pollingId.current);
		pollingId.current = null;
	}

	function checkForActiveItems(status: string, items: any) {
		for (const item of items) {
			const itemStatusPollingPeriod = PollingPeriods[status]?.itemStatusPollingPeriods?.[item.status];
			if (itemStatusPollingPeriod) return [true, itemStatusPollingPeriod];
		}
		return [false];
	}

	function handlePolling(status: string, orderItems: any) {
		//Handle Polling
		if (!PollingPeriods?.[status]) {
			if (pollingId.current) stopPolling();
			return;
		}
		let { pollingPeriod: newPollingPeriod, dependentonItemStatuses } = PollingPeriods[status];
		let currentPollingPeriod = pollingPeriod.current;
		let needsPolling;
		//If polling is dependent on the individual order item statuses.
		if (dependentonItemStatuses) {
			const [pollingRequired, itemStatusPollingPeriod]: any = checkForActiveItems(status, orderItems);
			newPollingPeriod = itemStatusPollingPeriod || newPollingPeriod;
			needsPolling = pollingRequired || !!newPollingPeriod;
		} else {
			needsPolling = !!newPollingPeriod;
		}
		//If polling is needed and polling hasn't been started
		if (needsPolling && !pollingId.current) {
			pollingPeriod.current = newPollingPeriod;
			startPolling();
		}
		//If polling period is changed update polling
		else if (needsPolling && newPollingPeriod !== currentPollingPeriod) {
			pollingPeriod.current = newPollingPeriod;
			updatePollingPeriod();
		}
		//No polling period found stop polling.
		else if (!needsPolling && pollingId.current) {
			stopPolling();
		}
	}

	/* Fetching Order Data */

	async function getOrderData(handleLoading = false) {
		if (handleLoading) setLoading(true);
		const response = await OrderService.trackOrder(orderId, uuid);
		const { data, error, metadata } = response;
		if (data) {
			let receivedOrderData = data;
			//Setting the order data to state
			setOrderData(receivedOrderData);
			setLoading(false);

			if (consecutiveErrors.current) consecutiveErrors.current = 0;
		}
		if (error) {
			//This will be handled only while polling and not on inital call failure.
			if(metadata && errorMetaDataHandler) {
				errorMetaDataHandler(metadata);
			} else {
				if (consecutiveErrors.current !== maxRequestErrorsAllowed && pollingId.current) {
					//Directly incrementing a ref gives out weird behaviour for some reason. TODO: Investigate.
					consecutiveErrors.current = consecutiveErrors.current + 1;
				} else {
					setError(error);
					setLoading(false);
					stopPolling();
				}
			}
		}
	}

	/* Render Functions */

	function renderDishes(dishes: any) {
		let renderData: any = [],
			dishCards = [];

		const orderDishes = dishes;

		for (const dish of orderDishes) {
			const { itemId, title, brandId, status, defaultRemoved, noDefaultAdded, etaText, userAllergens, brandDetails } = dish;
			let isDishReady = status === TaskStatuses.READY_TO_COLLECT;
			let brandImage = "";

			if (brandDetails) {
				const brandData = brandDetails.find((brand: any) => brand.id === brandId);
				if (brandData) {
					const { brandCms } = brandData;
					brandImage = brandCms?.brandLogo || "";
				}
			}

			//Check current taskStatus
			if (!(status in taskStatusMap)) {
				console.error(`Dish Status Invalid -- ${status}`);
				return;
			}
			const dishStatusConfig = taskStatusMap[status as keyof typeof taskStatusMap];
			const { statusMessage, showETA, color } = dishStatusConfig;
			
			//IMP: Don't generate unique keys on the fly.
			//Read : https://react.dev/learn/rendering-lists#keeping-list-items-in-order-with-key
			const jsxElementId = itemId; 

			dishCards.push(
				<Dish key={jsxElementId} active={isDishReady}>
					<BrandDetails>
						<BrandLogo src={brandImage} />
					</BrandDetails>
					<DishDetails>
						<Row>
							<DishTitle>{title}</DishTitle>
							<CenteredRow>
								<DishStatus id={isDishReady ? "status" : ""} color={color}>
									{showETA ? etaText : statusMessage}
								</DishStatus>
							</CenteredRow>
						</Row>
						<Line />
						<Row margin="1rem 0 0 0">
							<div style={{ width: "90%" }}>
								{renderDishContents({ dishData: dish, allergens: userAllergens, ignoreDietaryPreferences: true })}
							</div>
							{!isDishReady && (
								<Arrow onClick={() => toggleCard(itemId)} id={`${itemId}${arrowIdSuffix}`} src={chevronDownOutline} />
							)}
						</Row>
					</DishDetails>
				</Dish>
			);
		}

		return <CartItemsCard>{dishCards}</CartItemsCard>;
	}

	function renderOrderData(orderData: any) {
		let renderData = [];
		const { orderId, status, orderItems, restaurantId, restaurantName } = orderData;
		//Order Status Check
		if (!(status in orderStatusMap)) {
			console.error(`Order Status Invalid -- ${status}`);
			//Show Error Page
			return;
		}
		const orderStatusConfig = orderStatusMap[status as keyof typeof orderStatusMap];
		const { statusMessage, color } = orderStatusConfig;

		let itemsData = renderDishes(orderItems);

		renderData.push(
			<>
				<PageContainer>
					<OrderDetails>
						<KioskDetailsContainer>
							<KioskDetails>Kiosk {restaurantId}</KioskDetails>
							<KioskDetails>{restaurantName}</KioskDetails>
						</KioskDetailsContainer>
						<OrderStatus color={color}>{statusMessage}</OrderStatus>
					</OrderDetails>
					{itemsData}
				</PageContainer>
			</>
		);

		return renderData;
	}

	function renderActiveOrderData(orderData: any) {
		if (!orderData) return;

		let renderData = [];

		const { status } = orderData;

		switch (status) {
			case OrderStatuses.CREATED:
			case OrderStatuses.IN_QUEUE:
			case OrderStatuses.IN_PROGRESS:
				renderData.push(renderOrderData(orderData));
				break;

			case OrderStatuses.COMPLETED:
				renderData.push(renderOrderCompletedState(orderData));
				break;

			case OrderStatuses.PARTIALLY_COMPLETED:

				//NOTE : This is a very temporary change. Revert this ASAP.

				// renderData.push(renderPartiallyCompletedState(orderData));
				renderData.push(renderOrderCompletedState(orderData));
				break;

			case OrderStatuses.ABORTED:
			case OrderStatuses.FAILED:
				renderData.push(renderOrderAbortedState(orderData));
				break;

			default:
				break;
		}

        return renderData;

	}

	function toggleCard(id: string) {
		const couponCardText = document.getElementById(id + textIdSuffix);
		const couponCardArrow = document.getElementById(id + arrowIdSuffix);
		if (couponCardText && couponCardArrow) {
			const alreadyExpanded = couponCardText.classList.contains(cardTextExpandedClass);
			if (alreadyExpanded) {
				couponCardText.classList.remove(cardTextExpandedClass);
				couponCardArrow.classList.remove(cardArrowExpandedClass);
			} else {
				couponCardText.classList.add(cardTextExpandedClass);
				couponCardArrow.classList.add(cardArrowExpandedClass);
			}
		}
	}

	function renderOrderCompletedState(orderData: any) {
		const { orderId, restaurantId } = orderData;
		const { statusMessage, color } = orderStatusMap[OrderStatuses.COMPLETED];

		return (
			<>
				<CenteredContainer>
					<OrderDetails centered>
						<OrderId>Order {orderId}</OrderId>
					</OrderDetails>
					<OrderDetails centered>
						<KioskDetailsContainer>
							<KioskDetails>
								Kiosk ID : {restaurantId} {}
							</KioskDetails>
						</KioskDetailsContainer>
					</OrderDetails>

					<BigSpace />

					<StatusHead color={color}>{statusMessage}</StatusHead>
					<Icon version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130.2 130.2">
						<circle
							className="path circle"
							fill="none"
							stroke={color}
							stroke-width="6"
							stroke-miterlimit="10"
							cx="65.1"
							cy="65.1"
							r="62.1"
						/>
						<polyline
							className="path check"
							fill="none"
							stroke={color}
							stroke-width="6"
							stroke-linecap="round"
							stroke-miterlimit="10"
							points="100.2,40.2 51.5,88.8 29.8,67.5 "
						/>
					</Icon>

					<BigSpace />

					<Message>It was pleasure serving you. Hoping to see u order again soon...</Message>

					<SmallerSpace />

					<Link to={`/kiosk/${restaurantId}`}>Browse Menu</Link>
				</CenteredContainer>

				<div style={{ margin: "1.5rem 0 0 0", padding: "0 1rem" }} data-tf-live="01J515M78A7T3EF1NYQBKY0WHA" data-tf-hidden={`order_id=${orderId},src=app_details_page`}></div>

			</>
		);
	}

	function renderPartiallyCompletedState(orderData: any) {
		const { orderId, restaurantId } = orderData;
		const { statusMessage, color } = orderStatusMap[OrderStatuses.PARTIALLY_COMPLETED];

		return (
			<>
				<CenteredContainer>
					<OrderDetails centered>
						<OrderId>Order {orderId}</OrderId>
					</OrderDetails>
					<OrderDetails centered>
						<KioskDetailsContainer>
							<KioskDetails>
								Kiosk ID : {restaurantId} {}
							</KioskDetails>
						</KioskDetailsContainer>
					</OrderDetails>

					<BigSpace />

					<StatusHead color={color}>{statusMessage}</StatusHead>
					<Icon version="1.1" xmlns="http://www.w3.org/2000/svg">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							version="1.1"
							x="0px"
							y="0px"
							viewBox="0 0 256 256"
							enable-background="new 0 0 256 256">
							<g>
								<g>
									<g>
										<path
											stroke-width="2"
											fill={color}
											d="M116.9,10.3c-52.4,5-95.5,44.4-105,95.8c-1.4,7.8-1.8,12.6-1.8,21.9c0,19.9,4.1,36.8,13.1,54.2c5.9,11.4,12.4,20.4,21.5,29.4c9.5,9.5,19.3,16.5,30.7,22.2c26.2,12.9,54.2,15.8,82.7,8.5c8-2,13.8-4.2,22.2-8.4c29.1-14.2,51.5-40.5,60.9-71.5c5.6-18.6,6.5-39.7,2.5-59.1c-9.6-46.4-48.2-83.7-95-91.6C138,9.8,126.8,9.4,116.9,10.3z M137.7,25.1c48.5,4.7,87,42.4,93,91c0.6,5.3,0.6,18.5,0,23.7c-6.8,55.9-56.4,96.2-112.2,91c-41.1-3.7-76.4-32.1-89.1-71.7c-5.6-17.6-6.3-38.5-1.7-56.5c8.6-34.1,34.5-61.9,67.7-72.8C109.4,25.4,123.7,23.7,137.7,25.1z"
										/>
										<path
											fill={color}
											d="M117.1,87.4c0,12.2,0.1,14.4,2.5,38.8c1.4,14.2,2.5,26.1,2.5,26.3s2.6,0.4,5.8,0.4h5.7l0.2-1.5c0.1-0.8,1.3-12.5,2.6-25.9c2.2-22.9,2.3-25.3,2.3-37.8V74.4H128h-10.8L117.1,87.4L117.1,87.4z"
										/>
										<path stroke-width="6" fill={color} d="M118,170v10.2h9.9h9.9V170v-10.2H128H118V170z" />
									</g>
								</g>
							</g>
						</svg>
					</Icon>

					<BigSpace />

					<Message>
						We are sorry for the inconvenience caused. Rest assured we will be initiating a refund for the dishes that we couldn’t
						serve shortly.
					</Message>

					<BigSpace />

					<SmallMessage>You can track your refund status on your accounts page.</SmallMessage>
				</CenteredContainer>

				<div style={{ margin: "1.5rem 0 0 0", padding: "0 1rem" }} data-tf-live="01J515M78A7T3EF1NYQBKY0WHA" data-tf-hidden={`order_id=${orderId},src=app_details_page`}></div>
			</>
		);
	}

	function renderOrderAbortedState(orderData: any) {
		const { orderId, restaurantId } = orderData;
		const { statusMessage, color } = orderStatusMap[OrderStatuses.ABORTED];

		return (
			<>
				<CenteredContainer>
					<OrderDetails centered>
						<OrderId>Order {orderId}</OrderId>
					</OrderDetails>
					<OrderDetails centered>
						<KioskDetailsContainer>
							<KioskDetails>Kiosk ID : {restaurantId} </KioskDetails>
						</KioskDetailsContainer>
					</OrderDetails>

					<BigSpace />

					<StatusHead color={color}>{statusMessage}</StatusHead>
					<Icon version="1.1" xmlns="http://www.w3.org/2000/svg">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							version="1.1"
							x="0px"
							y="0px"
							viewBox="0 0 256 256"
							enable-background="new 0 0 256 256">
							<g>
								<g>
									<g>
										<path
											stroke-width="2"
											fill={color}
											d="M116.9,10.3c-52.4,5-95.5,44.4-105,95.8c-1.4,7.8-1.8,12.6-1.8,21.9c0,19.9,4.1,36.8,13.1,54.2c5.9,11.4,12.4,20.4,21.5,29.4c9.5,9.5,19.3,16.5,30.7,22.2c26.2,12.9,54.2,15.8,82.7,8.5c8-2,13.8-4.2,22.2-8.4c29.1-14.2,51.5-40.5,60.9-71.5c5.6-18.6,6.5-39.7,2.5-59.1c-9.6-46.4-48.2-83.7-95-91.6C138,9.8,126.8,9.4,116.9,10.3z M137.7,25.1c48.5,4.7,87,42.4,93,91c0.6,5.3,0.6,18.5,0,23.7c-6.8,55.9-56.4,96.2-112.2,91c-41.1-3.7-76.4-32.1-89.1-71.7c-5.6-17.6-6.3-38.5-1.7-56.5c8.6-34.1,34.5-61.9,67.7-72.8C109.4,25.4,123.7,23.7,137.7,25.1z"
										/>
										<path
											fill={color}
											d="M117.1,87.4c0,12.2,0.1,14.4,2.5,38.8c1.4,14.2,2.5,26.1,2.5,26.3s2.6,0.4,5.8,0.4h5.7l0.2-1.5c0.1-0.8,1.3-12.5,2.6-25.9c2.2-22.9,2.3-25.3,2.3-37.8V74.4H128h-10.8L117.1,87.4L117.1,87.4z"
										/>
										<path stroke-width="6" fill={color} d="M118,170v10.2h9.9h9.9V170v-10.2H128H118V170z" />
									</g>
								</g>
							</g>
						</svg>
					</Icon>

					<BigSpace />

					<Message>
						We are sorry for the inconvenience caused. Rest assured we will be initiating a refund for the dishes that we couldn’t
						serve shortly.
					</Message>

					<BigSpace />

					<SmallMessage>You can track your refund status on your accounts page.</SmallMessage>
				</CenteredContainer>

				<div
					style={{ margin: "1.5rem 0 0 0", padding: "0 1rem" }}
					data-tf-live="01KEWCEX12B3SJ9KSP1S1T70XV"
					data-tf-hidden={`order_id=${orderId},src=app_details_page`}></div>
			</>
		);
	}

	function renderCTAs() {
		let initialTime = "";

		if (hasActiveItem) initialTime = getInitialTime();

		return (
			<BottomContainer hide={!isShutterOpenVisible}>
				{hasActiveItem ? (
					<SecondaryCTA>
						<TimerComponent active={hasActiveItem}>
							<Time id="timer" ref={Timer}>
								{initialTime}
							</Time>
							<TimerText>Minutes Left to collect your plate</TimerText>
						</TimerComponent>
					</SecondaryCTA>
				) : (
					<ExpandableCTA
						customContainerStyle={{ bottom: "62px", position: "absolute" }}
						header={`You will have ${plateCollectionTime} to collect your plate`}
						content="We will notify you every time when your dish is ready. Please be around the machine to collect your dish or it will be auto discarded."
						icon={alert}
					/>
				)}
				<BottomCTA customCTAstyles={{ position: 'relative' }} disabled={isShutterOpenDisabled} onClick={openShutter}>
					COLLECT
				</BottomCTA>
			</BottomContainer>

		);
	}

	/* Shutter functionality handlers */

	function getActiveItem(orderItems: any) {
		for (const itemIndex in orderItems) {
			if (orderItems[itemIndex]?.status === TaskStatuses.READY_TO_COLLECT) {
				return { itemIndex, ...orderItems[itemIndex] };
			}
		}
		return null;
	}

	function handleCTAFunctionality(orderData: any) {
		if (!orderData) return;

		const { orderId, status, orderItems, collectTime } = orderData;

		const fancyTimeString = fancyTimeFormat(collectTime);
		setPlateCollectionTime(fancyTimeString);

		if (status !== OrderStatuses.IN_PROGRESS && status !== OrderStatuses.IN_QUEUE && status !== OrderStatuses.CREATED) {
			toggleShutterOpen(false);
			return;
		} else {
			toggleShutterOpen(true);
		}

		const activeItem = getActiveItem(orderItems);
		if (!activeItem) {
			if (isTimerRunning.current) {
				resetTimer();
			}
			disableShutterOpen(true);
			toggleHasActiveItem(false);
			return;
		}

		const { allowedCollectionStartTime, itemIndex } = activeItem;
        const now = new Date().getTime();

		//Check if allowedCollectionStartTime is a valid timestamp.

        if(!allowedCollectionStartTime || allowedCollectionStartTime < now) {
            disableShutterOpen(true);
        } else {
            if(isShutterOpenDisabled) disableShutterOpen(false);
            collectionTime.current = allowedCollectionStartTime;
            setShutterOpenPayload({ itemIndex, orderId });
            setupTimer()
        }
        toggleHasActiveItem(true);
        toggleShutterOpen(true);
	}

	/* Handling Timer */

    function resetTimer() {
        isTimerRunning.current = false;
        clearInterval(TimerInterval.current);
        disableShutterOpen(true);
    }

	function setupTimer() {
		if (isTimerRunning.current) return;
        startTimer(collectionTime.current);
	}

	function getMilliSecondsfromEpoch(epoch: any) {
		return new Date(epoch).getTime();
	}

    function handleTime(countDown:any){
        let countDownMs = getMilliSecondsfromEpoch(countDown);
        //Couldn't find the timer component.
        if (!Timer.current) {
            clearInterval(TimerInterval.current);
            return;
        }
        //Get the Diff in minutes and seconds.
        var text = "",
            now = new Date().getTime(),
            distance = countDownMs - now,
            minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
            seconds = Math.floor((distance % (1000 * 60)) / 1000);
        //User still has time to open the shutter.
        if (distance > 1000) {
            text = minutes < 10 ? "0" + minutes : minutes + "";
            text += ":";
            text += seconds < 10 ? "0" + seconds : seconds + "";
            isTimerRunning.current = true;
        } else {
            //Timer has ended.
            text = "00:00";
            resetTimer();
        }
        //Set Timer Text.
        Timer.current.innerHTML = text;
    }

    function getInitialTime() {
        let countDownMs = getMilliSecondsfromEpoch(collectionTime.current);

        //Get the Diff in minutes and seconds.
        var text = "",
        now = new Date().getTime(),
        distance = countDownMs - now,
        minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds = Math.floor((distance % (1000 * 60)) / 1000);
        //User still has time to open the shutter.
        if (distance > 1000) {
            text = minutes < 10 ? "0" + minutes : minutes + "";
            text += ":";
            text += seconds < 10 ? "0" + seconds : seconds + "";
            isTimerRunning.current = true;
        } else {
            //Timer has ended.
            text = "00:00";
        }
        return text;
    }

	function startTimer(countDown: number) {
		if (isTimerRunning.current) {
			return;
		}

		//Set the Timer Interval
		TimerInterval.current = setInterval(() => { handleTime(countDown) }, 1000);
	}

	//Call to Open Shutter
	async function openShutter() {
		disableShutterOpen(true);
		const openShutterResponse = await OrderService.openShutter(shutterOpenPayload, uuid);
		const { data, error } = openShutterResponse;
		if (data) {
			showShutterSuccessModal(true);
			//Call for data instantly to update the state of ther order.
			await getOrderData();
			handleShutterSuccess();
		}
		if (error || !data) {
			//Something went wrong in opening the shutter.
			ToastService.showToast("Shutter Open Failed");
			handleShutterError();
		}
	}

	async function handleShutterSuccess() {
		disableShutterOpen(false);
	}

	async function handleShutterError() {
		disableShutterOpen(false);
	}

	useLayoutEffect(() => {
		handleCTAFunctionality(orderData);
	}, [orderData]);

	return (
		<Page padding="0 0 1rem 0">
			<ActiveOrderMessage orderData={orderData} />
			{!error ? <OrderItems>{renderActiveOrderData(orderData)}</OrderItems> : ""}
			{!loading && !error ? (
				<PageContainer>
					<ATHS />
				</PageContainer>
			) : (
				""
			)}
			{!error ? renderCTAs() : ""}
			{isShutterOpenVisible ? <Gap value="7rem" /> : ""}
			{loading ? (
				<Center>
					<IonSpinner name="dots" color={"#F2F2F2"}></IonSpinner>
				</Center>
			) : (
				""
			)}
			{error ? (
				<Center>
					<ErrorComponent code={errorCode} />
				</Center>
			) : (
				""
			)}
			{shutterSuccessModal ?
				<ModalOverlay>
					<Modal>
						<ModalHeader>Mix n Enjoy</ModalHeader>
						<ModalSubtitle>
							We recommend mixing the bowl a bit to get every flavor in every bite.
						</ModalSubtitle>
						<ModalButtonGroup alignItems="right" >
							<ModalPrimaryButton
								onClick={() => {
									showShutterSuccessModal(false);
								}}>
								Okay
							</ModalPrimaryButton>
						</ModalButtonGroup>
					</Modal>
				</ModalOverlay> : ""}
		</Page>
	);
};

export default withNav(TrackOrder);
